import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { GameLink } from '../../shared/links.ts';
import { getEndpoints } from '../lib/config.ts';
import { asRtwError, type RtwError } from '../lib/errors.ts';
import { cacheStorageCache } from '../lib/fetcher.ts';
import { loadFiles, openGame, type GameDeps, type GameFiles, type OpenedGame } from '../lib/game.ts';
import { ErrorBox, Progress } from '../components.tsx';
import { formatBytes, PLATFORM_NAMES } from '../format.ts';
import { navigate } from '../router.ts';
import { Player } from './player.tsx';
import { LegalFooter } from './legal.tsx';
import { RomScreen, type ChosenRom } from './rom.tsx';
import { getSave, idbAvailable, putSave } from '../lib/db.ts';
import { netModel } from '../../shared/manifest.ts';
import { NetSession, roomUrl, type BootInfo } from '../net/session.ts';
import { inviteCode } from './online.tsx';

type Stage =
  | { s: 'opening' }
  | { s: 'info'; game: OpenedGame }
  | { s: 'downloading'; game: OpenedGame; loaded: number; total: number | null }
  | { s: 'ready'; game: OpenedGame; files: GameFiles }
  // boot (v1.2): this run is an online lockstep session boot with the room host's options and save
  | { s: 'playing'; game: OpenedGame; files: GameFiles; rom: ChosenRom; save?: Uint8Array; run: number; boot?: BootInfo }
  | { s: 'error'; error: RtwError; game?: OpenedGame };

export function GamePage({ link }: { link: GameLink }) {
  const deps: GameDeps = useMemo(() => ({ endpoints: getEndpoints(), cache: cacheStorageCache() }), []);
  const [stage, setStage] = useState<Stage>({ s: 'opening' });
  const [attempt, setAttempt] = useState(0);
  const [forceCommit, setForceCommit] = useState<string | undefined>();
  // v1.2 online play: an invite link (#room=CODE), the session (it outlives Player restarts), the player's own save
  const [invite, setInvite] = useState<string | null>(() => inviteCode(location.hash));
  const netRef = useRef<NetSession | null>(null);
  const ownSave = useRef<Uint8Array | undefined>(undefined);
  const localRef = useRef<(() => { options: [number, number][]; save?: Uint8Array }) | null>(null);
  useEffect(() => () => { netRef.current?.dispose(); }, []);
  const dropInvite = () => { setInvite(null); history.replaceState(history.state, '', location.pathname + location.search); };

  /** The session for this game and ROM (made when play starts; a new ROM means a new session). */
  const sessionFor = (game: OpenedGame, rom: ChosenRom): NetSession | undefined => {
    const m = game.manifest;
    const model = netModel(m);
    if (!model) return undefined;
    const cur = netRef.current as (NetSession & { key?: string }) | null;
    const key = `${game.buildId}/${rom.sha1}`;
    if (cur && cur.key === key) return cur;
    cur?.dispose();
    const net: NetSession & { key?: string } = new NetSession({
      buildId: game.buildId, romSha1: rom.sha1, players: m.net?.players ?? 2, model,
      roomUrl: (path) => roomUrl(path),
      local: () => localRef.current?.() ?? { options: [] },
      // lockstep: everyone reboots from power-on with the room host's options and save
      onBoot: (boot) => setStage((st) => (st.s === 'playing' ? { ...st, save: boot.save, boot, run: st.run + 1 } : st)),
      // left a lockstep session: play alone again, with this player's own save
      onEnd: () => setStage((st) => (st.s === 'playing' && st.boot ? { ...st, save: ownSave.current, boot: undefined, run: st.run + 1 } : st)),
    });
    net.key = key;
    netRef.current = net;
    return net;
  };

  useEffect(() => {
    let live = true;
    setStage({ s: 'opening' });
    openGame(link, deps, { forceCommit }).then(
      (game) => { if (live) { setStage({ s: 'info', game }); document.title = `${game.manifest.title} · retrowser`; } },
      (e) => { if (live) setStage({ s: 'error', error: asRtwError(e) }); },
    );
    return () => { live = false; };
  }, [attempt, forceCommit]);

  const download = (game: OpenedGame) => {
    setStage({ s: 'downloading', game, loaded: 0, total: game.totalSize });
    loadFiles(game, deps, (loaded, total) => setStage({ s: 'downloading', game, loaded, total })).then(
      (files) => setStage({ s: 'ready', game, files }),
      (e) => setStage({ s: 'error', error: asRtwError(e), game }),
    );
  };

  const game = 'game' in stage ? stage.game : undefined;
  return (
    <main class="game">
      <nav><a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>← retrowser</a></nav>
      {stage.s === 'opening' && <p class="muted" data-state="opening">Opening {link.owner}/{link.repo}…</p>}
      {game && <GameInfo game={game} />}
      {game?.updateCommit && stage.s === 'info' && (
        <div class="banner" data-state="update">
          New version available (commit {game.updateCommit.slice(0, 7)}) —{' '}
          <button onClick={() => setForceCommit(game.updateCommit)}>update?</button>
        </div>
      )}
      {stage.s === 'info' && (
        <button class="primary" data-action="download" onClick={() => download(stage.game)}>
          Download and continue ({formatBytes(stage.game.totalSize)})
        </button>
      )}
      {stage.s === 'downloading' && (
        <div data-state="downloading">
          <p>Downloading and checking game files… {formatBytes(stage.loaded)}{stage.total ? ` of ${formatBytes(stage.total)}` : ''}</p>
          <Progress loaded={stage.loaded} total={stage.total} />
        </div>
      )}
      {invite && stage.s !== 'playing' && stage.s !== 'error' && (
        <div class="banner" data-state="invite">
          You were invited to online room <strong>{invite}</strong>. Download the game and choose your ROM: you join
          the room once the game starts. Online, other players can see your IP address.{' '}
          <button onClick={dropInvite}>Don't join</button>
        </div>
      )}
      {stage.s === 'ready' && (
        <RomScreen manifest={stage.game.manifest} onRom={async (rom) => {
          const m = stage.game.manifest;
          const kind = m.save?.kind ?? 'none';
          const save = kind !== 'none' && idbAvailable() ? (await getSave(m.id, kind).catch(() => undefined))?.bytes : undefined;
          ownSave.current = save;
          sessionFor(stage.game, rom);
          setStage({ s: 'playing', game: stage.game, files: stage.files, rom, save, run: 0 });
        }} />
      )}
      {stage.s === 'playing' && (
        <Player key={stage.run} game={stage.game} files={stage.files} rom={stage.rom.bytes} save={stage.save}
          net={netRef.current ?? undefined} boot={stage.boot} localRef={localRef}
          invite={invite} onInviteUsed={dropInvite}
          onSave={async (bytes, generation) => {
            const m = stage.game.manifest;
            // a guest in a lockstep session plays on the room host's save: its own stored save stays untouched
            if (stage.boot && stage.boot.start.me !== 0) return;
            ownSave.current = bytes;
            if (m.save && m.save.kind !== 'none' && idbAvailable()) await putSave(m.id, m.save.kind, bytes, generation);
          }}
          onImportSave={async (bytes) => {
            // an imported save replaces the stored one; the game restarts with it
            const m = stage.game.manifest;
            if (m.save && m.save.kind !== 'none' && idbAvailable()) await putSave(m.id, m.save.kind, bytes, 0).catch(() => {});
            ownSave.current = bytes;
            netRef.current?.leave(); // an imported save restarts the game alone
            setStage({ ...stage, save: bytes, boot: undefined, run: stage.run + 1 });
          }}
          onExit={() => { netRef.current?.leave(); setStage({ s: 'ready', game: stage.game, files: stage.files }); }} />
      )}
      {stage.s === 'error' && <ErrorBox error={stage.error} onRetry={() => setAttempt((n) => n + 1)} />}
      {stage.s !== 'playing' && <LegalFooter />}
    </main>
  );
}

function allFilesCount(g: OpenedGame): number {
  return 1 + (g.manifest.files.js ? 1 : 0) + (g.manifest.files.data?.length ?? 0);
}

function GameInfo({ game }: { game: OpenedGame }) {
  const m = game.manifest;
  // only GitHub URLs are linked; anything else in the manifest falls back to the repository itself
  const source = m.source && /^https:\/\/github\.com\/[A-Za-z0-9-]+\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._\-/]*)?$/.test(m.source) ? m.source : `https://github.com/${game.link.owner}/${game.link.repo}`;
  return (
    <section class="info">
      <h1 data-field="title">{m.title}</h1>
      <dl>
        <dt>Platform</dt><dd>{PLATFORM_NAMES[m.platform] ?? m.platform}</dd>
        {m.license && <><dt>Licence</dt><dd>{m.license}</dd></>}
        <dt>Source</dt><dd><a href={source} target="_blank" rel="noopener noreferrer">{source}</a></dd>
        <dt>Commit</dt><dd><code data-field="commit">{game.commit.slice(0, 12)}</code>{game.offline ? ' (offline: last known version)' : ''}</dd>
        <dt>Download</dt><dd data-field="size">{formatBytes(game.totalSize)}{game.manifest.files.data?.length ? ` in ${allFilesCount(game)} files` : ''}</dd>
      </dl>
      <p class="muted" data-field="provenance">
        This game's code comes from <a href={source} target="_blank" rel="noopener noreferrer">{game.link.owner}/{game.link.repo}</a>,
        an independent project under its own licence. retrowser did not write it and does not host it; it runs in a
        sandbox in your browser, with a ROM you provide.
      </p>
    </section>
  );
}
